import React, { useState, useEffect } from "react"
import Breadcrumbs from "components/Common/Breadcrumb"
import {
  Badge,
  Card,
  CardBody,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  Table,
  UncontrolledDropdown,
} from "reactstrap"
import Filters from "components/Common/Filters"
import Switch from "components/Shared/Switch"
import { useDispatch } from "react-redux"
import { useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import Paginations from "components/Common/Paginations"
import { showAlert } from "store/reducers/alertReducer"
import DataExportDropdown from "components/Shared/DataExportDropdown"
import TableShimmer from "components/Shared/TableShimmer"
import DeleteModal from "components/Common/DeleteModal"
import { deleteFaq, getAllFaq, updateFaqStatus } from "store/actions/faqAction"
import { filters } from "components/Common/constants"
import { useTranslation } from "react-i18next"
import ConfirmModal from "components/Common/ConfirmModal"
import AppTypeTabs from "components/PageContent/AppTypeTabs"
import usePageContentAppType from "hooks/usePageContentAppType"
import { withAppTypeQuery } from "utils/pageContentPermissions"

const Faq = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("FAQ List")

  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const pageNumber = parseInt(searchParams.get("page")) || 1
  const [selectedId, setSelectedId] = useState(null)
  const [isApiCalled, setIsApiCalled] = useState(false)
  const [itemIdToDelete, setItemIdToDelete] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  const {
    loading,
    isLoading,
    totalCount,
    totalPages,
    currentPage,
    itemsPerPage,
    faqs,
  } = useSelector(state => state.Faqs)

  const {
    activeAppType,
    availableAppTypes,
    usersPermissions,
    setActiveAppType,
    hasAnyAccess,
  } = usePageContentAppType("faq")

  const [showStatusModal, setShowStatusModal] = useState(false)
  const [statusFaqId, setStatusFaqId] = useState(null)
  const [statusToUpdate, setStatusToUpdate] = useState(null)

  useEffect(() => {
    if (!hasAnyAccess) {
      navigate("/dashboard")
    }
  }, [hasAnyAccess, navigate])

  useEffect(() => {
    if (hasAnyAccess) {
      fetchFaqs()
    }
  }, [activeAppType, hasAnyAccess])

  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "")
  const [selectedFilters, setSelectedFilters] = useState({
    status: parseInt(searchParams.get("status") || "2"),
    sortOrder: parseInt(searchParams.get("sortOrder") || "1"),
  })

  const handleSearchChange = e => {
    setSearchTerm(e.target.value)
  }
  const handleFilterChange = (selectedValue, filterName) => {
    setSelectedFilters(prevFilters => ({
      ...prevFilters,
      [filterName]: selectedValue,
    }))
  }

  const fetchFaqs = () => {
    const { status, sortOrder } = selectedFilters

    const payload = {
      perPage: itemsPerPage,
      pageNo: pageNumber,
      status: status,
      orderBy: sortOrder,
      keyword: searchTerm,
      appType: activeAppType,
    }
    dispatch(getAllFaq(payload))
    setIsApiCalled(true)
  }

  const fetchFaqsApi = (page = 1) => {
    const { status, sortOrder } = selectedFilters;
  
    const searchParams = new URLSearchParams();
    searchParams.set("search", searchTerm);
    searchParams.set("status", status.toString());
    searchParams.set("sortOrder", sortOrder.toString());
    searchParams.set("page", page.toString());
    searchParams.set("appType", activeAppType);
  
    const payload = {
      perPage: itemsPerPage,
      pageNo: page,
      status: Number(status), // 0 deactive, 1 active, 2 for all
      orderBy: Number(sortOrder),
      keyword: searchTerm || "",
      appType: activeAppType,
    };
  
    dispatch(getAllFaq(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload;
        if (statusCode === 200 && status) {
          navigate(`/faq?${searchParams.toString()}`);
        }
      })
      .catch(error => {
        // Handle error if needed
      });
  };
  
  const handleSearch = () => {
    fetchFaqsApi(1);
  };
  
  const onPageChange = (page) => {
    fetchFaqsApi(page);
  };
  

  // useEffect(() => {
  //   if (isApiCalled) {
  //     if (searchTerm) {
  //       const delayDebounceFn = setTimeout(() => {
  //         handleSearch()
  //       }, 500)

  //       return () => clearTimeout(delayDebounceFn)
  //     } else {
  //       handleSearch()
  //     }
  //   }
  // }, [searchTerm, selectedFilters])

  const handleConfirmStatusUpdate = () => {
    if (!statusFaqId) return
    setSelectedId(statusFaqId)
    const payload = {
      id: statusFaqId,
      status: statusToUpdate,
      appType: activeAppType,
    }
    dispatch(updateFaqStatus(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
      .finally(() => {
        setShowStatusModal(false)
        setStatusFaqId(null)
        setStatusToUpdate(null)
      })
  }

  const handleDelete = () => {
    const payload = {
      id: itemIdToDelete,
      status: "2", // 2 FOR DELETE
      appType: activeAppType,
    }
    dispatch(deleteFaq(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          setShowDeleteModal(false)
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
  }

  const handleReset = () => {
    setSearchTerm("")
    const payload = {
      perPage: itemsPerPage,
      pageNo: 1,
      status: 2,
      orderBy: 1,
      keyword: "",
      appType: activeAppType,
    }
    dispatch(getAllFaq(payload))
    navigate(withAppTypeQuery("/faq", activeAppType, { page: 1 }))
    setSelectedFilters({
      status: 2,
      sortOrder: 1,
    })
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs />
          <Row>
            <Col xl={12}>
              <Card>
                <AppTypeTabs
                  activeAppType={activeAppType}
                  availableAppTypes={availableAppTypes}
                  onChange={setActiveAppType}
                />
                <DataExportDropdown
                  cardTitle={t("FAQ List")}
                  isAddBtn={loading ? false : usersPermissions.create}
                  addBtnText={t("Add FAQ")}
                  onAddBtnClick={() =>
                    navigate(withAppTypeQuery("/faq/add", activeAppType))
                  }
                />
                <CardBody>
                  <Filters
                    filters={filters}
                    searchTerm={searchTerm}
                    handleSearchChange={handleSearchChange}
                    handleFilterChange={handleFilterChange}
                    handleSearch={handleSearch}
                    onReset={handleReset}
                    selectedFilters={selectedFilters}
                    searchBy={t("Title")}
                  />
                  <div className="">
                    {loading ? (
                      <TableShimmer row={7} col={5} />
                    ) : faqs.length === 0 ? (
                      <p className="text-center my-2">{t("No faq found")}</p>
                    ) : (
                      <Table className="table mb-0" hover>
                        <thead className="table-light">
                          <tr>
                            <th>{t("S. No.")}</th>
                            <th>{t("Title(English)")}</th>
                            <th>{t("Title(عربي)")}</th>
                            <th>{t("Status")}</th>
                            {/* {usersPermissions.edit &&  */}
                            <th className="text-end">
                              {" "}
                              <span
                                className="d-inline-block "
                                style={{
                                  paddingRight: 20,
                                  display: "block",
                                }}
                              >
                                {t("Action")}
                              </span>
                            </th>
                            {/* } */}
                          </tr>
                        </thead>
                        <tbody>
                          {faqs.map((item, index) => (
                            <tr
                              key={item._id}
                              onClick={() => {
                                navigate(
                                  withAppTypeQuery("/faq/faq-details", activeAppType, {
                                    id: item._id,
                                  })
                                )
                              }}
                              className="cursor-pointer"
                            >
                              <th scope="row">
                                {" "}
                                {(currentPage - 1) * 10 + index + 1}
                              </th>
                              <td>{item.title}</td>
                              <td>{item.titleAr}</td>
                              <td>
                                {item.status === 1 ? (
                                  <Badge className="bg-success">
                                    {t("Active")}
                                  </Badge>
                                ) : (
                                  <Badge className="bg-danger">
                                    {t("Deactive")}
                                  </Badge>
                                )}
                              </td>
                              {/* {usersPermissions.edit && ( */}
                              <td className="text-end">
                                <div className="d-flex align-items-center justify-content-end gap-2">
                                  {usersPermissions.edit && (
                                    <Switch
                                      onClickHandler={e => {
                                        e.stopPropagation()
                                        setStatusFaqId(item._id)
                                        setStatusToUpdate(
                                          item.status === 1 ? "0" : "1"
                                        ) // toggle status
                                        setShowStatusModal(true)
                                      }}
                                      isChecked={item.status === 1}
                                      disabled={
                                        item._id === selectedId && isLoading
                                      }
                                    />
                                  )}
                                  <UncontrolledDropdown>
                                    <DropdownToggle
                                      tag="a"
                                      className="card-drop"
                                      onClick={e => e.stopPropagation()}
                                    >
                                      <i className="mdi mdi-dots-vertical font-size-18"></i>
                                    </DropdownToggle>
                                    <DropdownMenu className="dropdown-menu-end">
                                      {usersPermissions.edit && (
                                        <DropdownItem
                                          onClick={e => {
                                            e.stopPropagation()
                                            navigate(
                                              withAppTypeQuery("/faq/edit", activeAppType, {
                                                id: item._id,
                                              })
                                            )
                                          }}
                                        >
                                          <i className="mdi mdi-pencil font-size-16 text-success me-1"></i>
                                          {t("Edit")}
                                        </DropdownItem>
                                      )}
                                      <DropdownItem
                                        onClick={e => {
                                          e.stopPropagation()
                                          navigate(
                                            `/faq/faq-details?id=${item._id}`
                                          )
                                        }}
                                      >
                                        <i className="mdi mdi-eye font-size-16 text-primary me-1"></i>
                                        {t("View")}
                                      </DropdownItem>
                                      {usersPermissions.delete && (
                                        <DropdownItem
                                          onClick={e => {
                                            e.stopPropagation()
                                            setItemIdToDelete(item._id)
                                            setShowDeleteModal(true)
                                          }}
                                        >
                                          <i className="mdi mdi-trash-can font-size-16 text-danger me-1"></i>
                                          {t("Delete")}
                                        </DropdownItem>
                                      )}
                                    </DropdownMenu>
                                  </UncontrolledDropdown>
                                </div>
                              </td>
                              {/* )} */}
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </div>
                  {!loading && faqs.length > 0 && (
                    <div className="mt-3">
                      <Paginations
                        data={faqs}
                        itemsPerPage={itemsPerPage}
                        onPageChange={onPageChange}
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalCount={totalCount}
                      />
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <DeleteModal
        show={showDeleteModal}
        isLoading={isLoading}
        onDeleteClick={handleDelete}
        onCloseClick={() => setShowDeleteModal(false)}
        buttonText={t("Delete")}
        bodyTitle={t("Are you sure you want to delete this {{name}}?", {
          name: t("FAQ"),
        })}
      />
      <ConfirmModal
        show={showStatusModal}
        isLoading={isLoading}
        onDeleteClick={handleConfirmStatusUpdate}
        onCloseClick={() => setShowStatusModal(false)}
        buttonText={t("Yes")}
        bodyTitle={t("Are you sure you want to update the status?")}
      />
    </React.Fragment>
  )
}

export default Faq
